import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ApiService } from '../../services/api.service';
import { UserAuthService } from '../../services/user-auth.service';
import { ChatMessage, UsageLimitInfo } from '../../models/rag.model';
import { AuthenticatedUser } from '../../models/client-user.model';

@Component({
  selector: 'app-rag-chat',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './rag-chat.component.html',
  styleUrls: ['./rag-chat.component.scss']
})
export class RagChatComponent implements OnInit, AfterViewChecked {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;

  user: AuthenticatedUser | null = null;
  messages: ChatMessage[] = [];
  userInput: string = '';
  isLoading: boolean = false;
  usageInfo: UsageLimitInfo | null = null;
  private messageIdCounter = 0;
  private shouldScroll = false;

  constructor(
    private apiService: ApiService,
    private authService: UserAuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.user = this.authService.getCurrentUser();
    if (!this.user) {
      this.router.navigate(['/login']);
      return;
    }
    this.loadUsageInfo();
    this.addWelcomeMessage();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  private addWelcomeMessage(): void {
    this.messages.push({
      id: this.messageIdCounter++,
      role: 'assistant',
      content: 'Hello! I\'m your AI assistant. How can I help you today?',
      timestamp: new Date()
    });
  }

  loadUsageInfo(): void {
    if (this.authService.isAuthenticated()) {
      this.apiService.getRagUsage().subscribe({
        next: (usage) => this.usageInfo = usage,
        error: (err) => console.error('Failed to load usage info', err)
      });
    }
  }

  async sendMessage(): Promise<void> {
    if (!this.userInput.trim() || this.isLoading) return;

    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/login']);
      return;
    }

    if (this.usageInfo?.limitReached) {
      this.messages.push({
        id: this.messageIdCounter++,
        role: 'assistant',
        content: 'You have reached your daily query limit. Please try again tomorrow.',
        timestamp: new Date(),
        error: true
      });
      this.shouldScroll = true;
      return;
    }

    const userMessage = this.userInput.trim();
    this.userInput = '';

    // Add user message
    this.messages.push({
      id: this.messageIdCounter++,
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    });

    // Add loading message
    const loadingId = this.messageIdCounter++;
    this.messages.push({
      id: loadingId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isLoading: true
    });
    this.shouldScroll = true;
    this.isLoading = true;

    this.apiService.ragAsk({ query: userMessage }).subscribe({
      next: (response) => {
        const loadingIdx = this.messages.findIndex(m => m.id === loadingId);
        if (loadingIdx !== -1) {
          this.messages[loadingIdx] = {
            id: loadingId,
            role: 'assistant',
            content: response.success
              ? (response.response || 'No response')
              : (response.error || 'Sorry, I couldn\'t process your request. Please try again.'),
            timestamp: new Date(),
            error: !response.success
          };
        }
        this.isLoading = false;
        // Only refresh usage info on success (errors don't count against limit)
        if (response.success) {
          this.loadUsageInfo();
        }
        this.shouldScroll = true;
      },
      error: (err) => {
        const loadingIdx = this.messages.findIndex(m => m.id === loadingId);
        if (loadingIdx !== -1) {
          this.messages[loadingIdx] = {
            id: loadingId,
            role: 'assistant',
            content: this.getErrorMessage(err),
            timestamp: new Date(),
            error: true
          };
        }
        this.isLoading = false;
        this.shouldScroll = true;
      }
    });
  }

  private getErrorMessage(err: any): string {
    if (err.error?.error) {
      return err.error.error;
    }
    if (err.status === 0) {
      return 'Unable to connect to the server. Please check your internet connection.';
    }
    if (err.status === 401) {
      return 'Your session has expired. Please log in again.';
    }
    if (err.status === 500) {
      return 'The server encountered an error. Please try again later.';
    }
    return 'Sorry, something went wrong. Please try again.';
  }

  private scrollToBottom(): void {
    if (this.messagesContainer) {
      this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
    }
  }

  onLogout(): void {
    this.apiService.userLogout().subscribe({
      next:  () => { this.authService.clearLocalState(); this.router.navigate(['/login']); },
      error: () => { this.authService.clearLocalState(); this.router.navigate(['/login']); }
    });
  }

  get displayName(): string {
    if (this.user?.firstName || this.user?.lastName) {
      return [this.user.firstName, this.user.lastName].filter(Boolean).join(' ');
    }
    return this.user?.email || 'User';
  }
}
